import {
  takeLeading, put, select, call, takeLatest,
} from 'redux-saga/effects';
import { config } from '@/config';
import { api } from '@/utils/api';
import agentsFactoryActions from './agentsFactory.actions';
import { AgentsFactoryActionTypes } from './agentsFactory.types';
import logger from '@/utils/logger';
import { deleteCookie, getCookie, setCookie } from '@/utils/Application/cookies';

const { WHMCS_URL, AGENT_FACTORY_ORCHESTRATOR_API_URL } = config;

const AGENT_FACTORY_AUTH_COOKIE = 'agent-factory-auth';

const parseAgentFactoryAuthCookie = () => {
  const cookieValue = getCookie(AGENT_FACTORY_AUTH_COOKIE);
  if (!cookieValue) return null;
  try {
    return JSON.parse(cookieValue);
  } catch (e) {
    return null;
  }
};

export const signup = function* signup() {
  const { token } = yield select(state => state.auth);
  const { id: clientId } = yield select(state => state.summary);

  const existingAuth = parseAgentFactoryAuthCookie();
  if (existingAuth && existingAuth.client_id && existingAuth.client_id === clientId) {
    return;
  }
  if (existingAuth) {
    deleteCookie(AGENT_FACTORY_AUTH_COOKIE);
  }

  const request = {
    url: `${WHMCS_URL}/api/v3/agent-factory/signup`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(agentsFactoryActions.signup.success(response.data));

    setCookie(AGENT_FACTORY_AUTH_COOKIE, JSON.stringify({
      ...response.data.data,
      client_id: clientId,
    }));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas signup - method signup', error);
    yield put(agentsFactoryActions.signup.failure(error));
  }
};

export const improveTextWithAI = function* improveTextWithAI({ payload }) {
  const { prompt } = payload;
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth && agentFactoryAuth.access_token;

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/agent-prompt/improve`,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: {
      prompt,
    },
  };

  yield put(agentsFactoryActions.improveTextWithAI.loading(true));

  try {
    const response = yield call(() => fetch(request.url, {
      method: 'POST',
      headers: {
        Authorization: request.headers.Authorization,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request.data),
    }).then(res => res.json()));
    yield put(agentsFactoryActions.improveTextWithAI.success());

    let improvedText = '';
    if (response && response.data && response.data.prompt) {
      improvedText = response.data.prompt;
    }

    if (!improvedText) {
      improvedText = prompt;
    }

    yield put(agentsFactoryActions.improveTextWithAI.loading(false));
    yield put(agentsFactoryActions.improveTextWithAI.setImprovedText(improvedText));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas improveTextWithAI - method improveTextWithAI', error);
    yield put(agentsFactoryActions.improveTextWithAI.failure());
    yield put(agentsFactoryActions.improveTextWithAI.loading(false));
  }
};

export const create = function* create({ payload, onSuccess }) {
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth.access_token;
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (value instanceof File) {
        formData.append(key, value, value.name);
      } else {
        formData.append(key, String(value));
      }
    }
  });

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/agent`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  };

  try {
    const response = yield call(() => fetch(request.url, {
      method: 'POST',
      headers: {
        Authorization: request.headers.Authorization,
      },
      body: request.data,
    }).then(res => res.json()));
    yield put(agentsFactoryActions.create.success(response.data));
    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas create agent - method create agent', error);
    yield put(agentsFactoryActions.create.failure(error));
    throw error;
  }
};

export const installAiAgent = function* installAiAgent({ payload, onSuccess }) {
  const { token } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/builder/install-ai-agent`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: payload,
  };

  try {
    const response = yield call(api.external.patch, request);
    logger.info('Install AI agent response:', response);
    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas installAiAgent - method installAiAgent', error);
    throw error;
  }
};

export const activate = function* activate({ domain, agentId, onSuccess }) {
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth.access_token;

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/agent/${agentId}/activate`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      domain,
    },
  };

  try {
    const response = yield call(api.external.patch, request);
    logger.info('Activate agent response:', response);
    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data.data;
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas activate agent - method activate agent', error);
    throw error;
  }
};

export const changeStatus = function* changeStatus(action) {
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth.access_token;
  const { status, agentId } = action.payload;

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/agent/${agentId}/status`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: { active: status },
  };

  try {
    const response = yield call(api.external.patch, request);
    yield put(agentsFactoryActions.changeStatus.success(response.data));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas change status agent - method changeStatus', error);
    yield put(agentsFactoryActions.changeStatus.failure(error));
  }
};

export const createDatabase = function* createDatabase({ payload, onSuccess }) {
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth.access_token;
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (value instanceof File) {
        formData.append(key, value, value.name);
      } else {
        formData.append(key, String(value));
      }
    }
  });

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/database`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  };

  try {
    const response = yield call(() => fetch(request.url, {
      method: 'POST',
      headers: {
        Authorization: request.headers.Authorization,
      },
      body: request.data,
    }).then(res => res.json()));
    yield put(agentsFactoryActions.createDatabase.success(response.data));
    if (onSuccess) {
      onSuccess(response.data);
    }
    return response.data;
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas createDatabase - method createDatabase', error);
    yield put(agentsFactoryActions.createDatabase.failure(error));
    throw error;
  }
};

function* sagas() {
  yield takeLeading(AgentsFactoryActionTypes.AGENTS_FACTORY_SIGNUP_REQUEST, signup);
  yield takeLeading(AgentsFactoryActionTypes.AGENTS_FACTORY_CREATE_REQUEST, create);
  yield takeLeading(AgentsFactoryActionTypes.AGENTS_FACTORY_CHANGE_STATUS_REQUEST, changeStatus);
  yield takeLeading(AgentsFactoryActionTypes.AGENTS_FACTORY_CREATE_DATABASE_REQUEST, createDatabase);
  yield takeLatest(AgentsFactoryActionTypes.AGENTS_FACTORY_IMPROVE_TEXT_WITH_AI_REQUEST, improveTextWithAI);
}

export default sagas;
